// frontend/src/components/documents/DocumentManager.js
// Phase 3: Document Management Component

import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton,
  List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Skeleton
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableViewIcon from '@mui/icons-material/TableView';
import CodeIcon from '@mui/icons-material/Code';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudIcon from '@mui/icons-material/Cloud';
import FolderIcon from '@mui/icons-material/Folder';
import WarningIcon from '@mui/icons-material/Warning';
import axios from 'axios';

const DocumentManager = ({ brandId, expertId, agentId, onDocumentDeleted }) => {
  const ownerId = agentId || expertId;
  const ownerType = agentId ? 'agent' : 'expert';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [viewDialog, setViewDialog] = useState(null);
  const [sourceSystem, setSourceSystem] = useState(null);

  const getFileIcon = (contentType) => {
    if (contentType.includes('pdf')) return <PictureAsPdfIcon color="error" />;
    if (contentType.includes('wordprocessingml') || contentType.includes('msword')) return <ArticleIcon color="primary" />;
    if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return <TableViewIcon color="success" />;
    if (contentType.includes('json')) return <CodeIcon color="warning" />;
    return <InsertDriveFileIcon color="action" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate that ownerId is provided
      if (!ownerId) {
        setError('Agent must be selected to view documents');
        setDocuments([]);
        return;
      }

      console.log(`[DocumentManager] Loading documents for brand ${brandId}, ${ownerType} ${ownerId}`);

      // Use owner-specific endpoint with ownerType param
      const response = await axios.get(`/api/chat/documents/${brandId}/${ownerId}?ownerType=${ownerType}`);
      
      if (response.data && response.data.documents) {
        setDocuments(response.data.documents);
        setSourceSystem(response.data.source_system);
        console.log(`[DocumentManager] Loaded ${response.data.documents.length} documents for ${ownerType} ${ownerId} (${response.data.source_system})`);
      } else {
        setDocuments([]);
        setSourceSystem(null);
      }
    } catch (error) {
      console.error('[DocumentManager] Failed to load documents:', error);
      setError(error.response?.data?.error || 'Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (brandId && ownerId) {
      loadDocuments();
    } else if (brandId && !ownerId) {
      setError('Agent must be selected to view documents');
      setDocuments([]);
      setLoading(false);
    }
  }, [brandId, ownerId]);

  const handleDelete = async (documentId) => {
    try {
      console.log(`[DocumentManager] Deleting document ${documentId}`);
      
      await axios.delete(`/api/chat/documents/${brandId}/${documentId}`);
      
      // Remove from local state
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setDeleteDialog(null);
      
      console.log(`[DocumentManager] Document ${documentId} deleted successfully`);
      onDocumentDeleted?.(documentId);
    } catch (error) {
      console.error('[DocumentManager] Delete failed:', error);
      setError(error.response?.data?.error || 'Failed to delete document');
    }
  };

  const handleView = (document) => {
    setViewDialog(document);
  };

  if (!brandId) {
    return (
      <Alert severity="info">
        Select a brand to view its documents.
      </Alert>
    );
  }
  
  if (!ownerId) {
    return (
      <Alert severity="warning">
        Select an agent to view their documents. Each agent has their own isolated knowledge base.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Agent Documents ({documents.length})
          </Typography>
          {sourceSystem && (
            <Chip 
              label={sourceSystem === 'gemini_file_search' ? 'File Search' : 'Legacy'}
              icon={sourceSystem === 'gemini_file_search' ? <CloudIcon /> : <FolderIcon />}
              size="small"
              color={sourceSystem === 'gemini_file_search' ? 'primary' : 'default'}
              variant="outlined"
            />
          )}
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadDocuments}
          size="small"
          disabled={!ownerId}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                  <Skeleton variant="rectangular" width={60} height={24} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <InsertDriveFileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Documents for This Agent
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload business documents to build this agent's knowledge base. Each agent has their own isolated document collection.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {documents.map((document) => (
            <ListItem
              key={document.id}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.paper'
              }}
            >
              <ListItemIcon>
                {getFileIcon(document.content_type)}
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {document.filename}
                    </Typography>
                    <Chip
                      label={document.status}
                      size="small"
                      color={getStatusColor(document.status)}
                      sx={{ minWidth: 'auto' }}
                    />
                    {document.source === 'gemini_file_search' && (
                      <Chip 
                        label="AI Search"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ minWidth: 'auto' }}
                      />
                    )}
                    {document.error_message && (
                      <Chip 
                        icon={<WarningIcon />}
                        label="Error"
                        size="small"
                        color="error"
                        sx={{ minWidth: 'auto' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" display="block">
                      {formatFileSize(document.file_size)} • {document.source === 'gemini_file_search' ? 'AI-indexed' : `${document.chunk_count || 0} chunks`} • Expert: {document.expert_id ? 'Assigned' : 'Shared'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Uploaded: {formatDate(document.created_at)}
                    </Typography>
                    {document.error_message && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                        ⚠ {document.error_message}
                      </Typography>
                    )}
                  </Box>
                }
              />
              
              <ListItemSecondaryAction>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleView(document)}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => { setError(''); setDeleteDialog(document); }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteDialog)}
        onClose={() => setDeleteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Typography>
            Are you sure you want to delete "{deleteDialog?.filename}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {deleteDialog?.source === 'gemini_file_search' 
              ? 'This will remove the document from AI File Search. This action cannot be undone.'
              : 'This will also remove all associated knowledge chunks and cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button
            onClick={() => handleDelete(deleteDialog.id)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog
        open={Boolean(viewDialog)}
        onClose={() => setViewDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          {viewDialog && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {getFileIcon(viewDialog.content_type)}
                <Box>
                  <Typography variant="h6">{viewDialog.filename}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {viewDialog.content_type}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">File Size</Typography>
                  <Typography variant="body2">{formatFileSize(viewDialog.file_size)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={viewDialog.status} 
                      size="small" 
                      color={getStatusColor(viewDialog.status)} 
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Storage System</Typography>
                  <Typography variant="body2">
                    {viewDialog.source === 'gemini_file_search' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <CloudIcon fontSize="small" color="primary" />
                        <span>AI File Search</span>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <FolderIcon fontSize="small" />
                        <span>{viewDialog.chunk_count || 0} chunks</span>
                      </Box>
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Uploaded</Typography>
                  <Typography variant="body2">{formatDate(viewDialog.created_at)}</Typography>
                </Box>
              </Box>
              
              {viewDialog.status === 'ready' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Document has been processed and is available to experts.
                </Alert>
              )}
              
              {viewDialog.status === 'processing' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Document is being processed and will be available to experts shortly.
                </Alert>
              )}
              
              {viewDialog.status === 'failed' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Document processing failed. You may need to re-upload the file.
                  {viewDialog.error_message && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Error: {viewDialog.error_message}
                    </Typography>
                  )}
                </Alert>
              )}
              
              {viewDialog.source === 'gemini_file_search' && viewDialog.gemini_file_id && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    File Search Details
                  </Typography>
                  <Typography variant="caption" display="block">
                    File ID: {viewDialog.gemini_file_id}
                  </Typography>
                  {viewDialog.gemini_store_name && (
                    <Typography variant="caption" display="block">
                      Store: {viewDialog.gemini_store_name}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentManager;
